import {
  getVendorSettings,
  setVendorData,
  getAPIURL,
  getUserID,
  getVendorID,
  getGlobalSettings,
  roundToNearestNickel,
} from "../services/helper";
import axios from "axios";
import { toast } from "react-toastify";
import { addNewOrder } from "../ReduxToolkit/Reducers/OrdersReducer";
import {
  revokeDiscount,
  setPosItems,
} from "../ReduxToolkit/Reducers/CartReducer";
import {
  redeemCustomerPoints,
  setBackgroundSyncing,
} from "../ReduxToolkit/Reducers/AppReducer";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";

// API endpoint
const api_url = getAPIURL();

// Fetch orders from localStorage
const getOrdersFromLocalStorage = () => {
  const orders = localStorage.getItem("tealbo_pos_orders");
  return orders ? JSON.parse(orders) : [];
};

// Save orders back to localStorage
const saveOrdersToLocalStorage = (orders) => {
  localStorage.setItem("tealbo_pos_orders", JSON.stringify(orders));
};

export const syncOrders = async (dispatch) => {
  dispatch(setBackgroundSyncing(true)); // 🔒 Lock sync

  try {
    let orders = getOrdersFromLocalStorage();

    if (orders.length > 0) {
      const originalOrder = orders[0];
      let order = {
        ...originalOrder,
        vendor_id: getVendorID(),
        biller_id: getUserID(),
      };

      // Apply government penny rounding to tax if enabled (vendor setting takes precedence)
      const allowGovtRounding = getVendorSettings("allow_govt_discount") === true;
      
      if (allowGovtRounding && originalOrder.tax_before_rounding) {
        const taxAfterRounding = parseFloat(originalOrder.tax || 0);
        const taxBeforeRounding = parseFloat(originalOrder.tax_before_rounding || 0);
        const taxRoundingAdjustment = parseFloat((taxAfterRounding - taxBeforeRounding).toFixed(2));
        
        order = {
          ...order,
          tax_rounding_adjustment: taxRoundingAdjustment,
        };
      }

      const response = await axios.post(`${api_url}/create-pos-order`, order);

      // Always shift the processed order from the queue
      orders.shift();
      saveOrdersToLocalStorage(orders);

      if (response.status === 200) {
        const { data: new_order } = response;

        if (!new_order.success) {
          toast.error(
            Array.isArray(new_order.data?.errors)
              ? new_order.data.errors.map((e) => e.message).join("\n")
              : new_order.data || "Failed to create order"
          );

          // ❌ Remove errored products from POS
          if (Array.isArray(new_order.data?.errors)) {
            const errorProductIds = new_order.data.errors.map(
              (e) => e.product_id
            );

            // Get current POS items from vendor settings
            const currentPOSItemsString = getGlobalSettings("pos_items");
            let currentPOSItems = [];

            try {
              currentPOSItems = JSON.parse(currentPOSItemsString) || [];
            } catch (e) {
              console.warn("Failed to parse vendor pos_items:", e);
            }

            // Filter out errored products
            const updatedPOSItems = currentPOSItems.filter(
              (item) => !errorProductIds.includes(item.id)
            );

            // Save updated items back to vendor settings
            await setVendorData({ pos_items: JSON.stringify(updatedPOSItems) });

            // Dispatch to update Redux state
            dispatch(setPosItems(updatedPOSItems));
          }
        } else {
          // ✅ Success - store order and handle rewards
          dispatch(addNewOrder(new_order.data));
          dispatch(revokeDiscount());

          if (order.redeem_points) {
            dispatch(redeemCustomerPoints(order.customer_id));
          }
        }
      }
    }
  } catch (error) {
    console.error("❌ Error syncing order:", error);

    // Remove the errored order from the queue
    let orders = getOrdersFromLocalStorage();
    orders.shift();
    saveOrdersToLocalStorage(orders);
  } finally {
    dispatch(setBackgroundSyncing(false)); // 🔓 Unlock
  }
};

export default function BackgroundOrdersSync() {
  const dispatch = useDispatch();
  const { isBackgroundWorking } = useSelector((state) => state.app);
  const { posItems } = useSelector((state) => state.cart);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isBackgroundWorking) {
        syncOrders(dispatch);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [dispatch, isBackgroundWorking]); // 💡 include `isBackgroundWorking` in deps

  return null; // No UI component needed for background sync
}
